import { Injectable } from '@angular/core';
import { UserManager } from 'oidc-client';
import { Observable, firstValueFrom, from, map, of, switchMap, tap } from 'rxjs';
import { ConfigService } from 'src/shared/services/config.service';
import { JwtService } from './jwt.service';
import { AuthenticationMethods } from 'src/shared/constants';
import { Router } from '@angular/router';
import { AuthClient, CreateNonProdUserTokenCommand, TokenRefreshResult } from 'src/shared/services/api.service';
import { PendingApplicationService } from 'src/shared/services/pending-application.service';
import { ShepherdService } from 'angular-shepherd';
import { TourService } from 'src/shared/services/tour.service';
import { ScreenShareService } from 'src/shared/services/screen-share.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _userManager!: UserManager;

  constructor(
    private readonly configService: ConfigService,
    private readonly jwtService: JwtService,
    private readonly router: Router,
    private readonly authClient: AuthClient,
    private readonly pendingAppService: PendingApplicationService,
    private readonly shepherdService: ShepherdService,
    private readonly tourService: TourService,
    private readonly screenShareService: ScreenShareService,
  ) {}

  get userManager(): UserManager {
    return (
      this._userManager ??
      new UserManager({
        authority: this.configService.config.azureAppConfig.authority!,
        metadataUrl: this.configService.config.azureAppConfig.metadataUrl,
        client_id: this.configService.config.azureAppConfig.clientId!,
        redirect_uri: this.configService.config.azureAppConfig.redirectUri!,
        scope: 'openid',
        response_type: 'code',
        post_logout_redirect_uri: this.configService.config.azureAppConfig.postLogoutRedirectUri,
        prompt: 'login',
      })
    );
  }

  async login(): Promise<void> {
    return this.userManager.signinRedirect();
  }

  async logout(): Promise<void> {
    const userId = this.jwtService.userId;
    await firstValueFrom(
      this.authClient.logout({
        userId: userId,
      }),
    );
    const isTestLogin = this.jwtService.authMethod === AuthenticationMethods.test;
    this.jwtService.removeToken();
    this.screenShareService.disconnect();
    if (isTestLogin) {
      this.router.navigateByUrl('/launch');
    } else {
      this.userManager.signoutRedirect();
    }

    // Close any tour screen that might be left open
    if (this.shepherdService.isActive) {
      this.shepherdService.hide();
    }

    this.tourService.cleanupTourLocalStorage();
  }

  isLoggedIn(): Observable<boolean> {
    const isTokenValid = this.jwtService.getToken() && this.jwtService.isTokenRenewable();

    return of(!!isTokenValid);
  }

  refreshToken(): Observable<TokenRefreshResult> {
    return this.authClient.refreshToken().pipe(tap((x) => this.jwtService.setToken(x.token!)));
  }

  completeTestLogin(ein: string): Observable<boolean> {
    const command: CreateNonProdUserTokenCommand = {
      ein: ein,
    };
    return this.authClient.createNonProdUserToken(command).pipe(
      map((res) => {
        this.jwtService.setToken(res.localToken!);
        this.userManager.removeUser();
        this.screenShareService.connect();

        // alert that a pending app was cleared.
        this.pendingAppService.pendingAppClearedSubject.next(res.wasPendingAppClearedSinceLastLogin);

        return res.isEducatorCorrelated;
      }),
    );
  }

  completeLogin(): Observable<boolean> {
    return from(this.userManager.signinRedirectCallback())
      .pipe(
        switchMap((idpUser) => {
          return this.authClient.exchangeIdpToken({
            idpIdentityToken: idpUser.id_token!,
          });
        }),
      )
      .pipe(
        map((res) => {
          this.jwtService.setToken(res.localToken!);
          // Remove the idp user info if we've exchanged the token.
          this.userManager.removeUser();
          this.screenShareService.connect();

          // alert that a pending app was cleared.
          this.pendingAppService.pendingAppClearedSubject.next(res.wasPendingAppClearedSinceLastLogin);

          return res.isEducatorCorrelated;
        }),
      );
  }

  completeLogout() {
    return this.userManager.signoutRedirectCallback();
  }
}
